import apiClient from '../../shared/services/apiClient';

/**
 * User Service (Admin Version)
 *
 * Handles admin-level user management operations.
 * Admins can manage all users, reset passwords, and adjust balances.
 *
 * All routes use /api/admin/users/* prefix
 */
class UserService {
  // ==========================================
  // User Management
  // ==========================================

  /**
   * Get all users
   * @param {object} params - Query parameters (page, search, status, etc.)
   */
  async getUsers(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/users', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single user
   * @param {number} userId - User ID
   */
  async getUser(userId) {
    try {
      const response = await apiClient.get('/api/admin/users/get', {
        params: { id: userId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create user
   * @param {object} userData - User data (name, email, password, etc.)
   */
  async createUser(userData) {
    try {
      const response = await apiClient.post('/api/admin/users/create', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update user
   * @param {object} userData - User data with id
   */
  async updateUser(userData) {
    try {
      const response = await apiClient.put('/api/admin/users/update', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete user
   * @param {number} userId - User ID
   */
  async deleteUser(userId) {
    try {
      const response = await apiClient.delete('/api/admin/users/delete', {
        params: { id: userId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Password Management
  // ==========================================

  /**
   * Reset user password (admin action)
   * @param {number} userId - User ID
   * @param {string} newPassword - Optional new password (auto-generated if not provided)
   */
  async resetPassword(userId, newPassword = null) {
    try {
      const data = { id: userId };
      if (newPassword) data.new_password = newPassword;

      const response = await apiClient.post('/api/admin/users/reset-password', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Balance Management
  // ==========================================

  /**
   * Get user balance
   * @param {number} userId - User ID
   */
  async getUserBalance(userId) {
    try {
      const response = await apiClient.get('/api/admin/users/balance', {
        params: { id: userId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Adjust user balance
   * @param {number} userId - User ID
   * @param {number} amount - Amount to adjust (positive or negative)
   * @param {string} description - Reason for adjustment
   */
  async adjustBalance(userId, amount, description) {
    try {
      const response = await apiClient.post('/api/admin/users/adjust-balance', {
        id: userId,
        amount: amount,
        description: description
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get user balance history
   * @param {number} userId - User ID
   * @param {object} params - Query parameters
   */
  async getBalanceHistory(userId, params = {}) {
    try {
      const response = await apiClient.get('/api/admin/users/balance/history', {
        params: { user_id: userId, ...params }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Activity & Logs
  // ==========================================

  /**
   * Get user activity log
   * @param {number} userId - User ID
   * @param {object} params - Query parameters
   */
  async getUserActivity(userId, params = {}) {
    try {
      const response = await apiClient.get('/api/admin/users/activity', {
        params: { user_id: userId, ...params }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // API Key Management (Admin view)
  // ==========================================

  /**
   * Get user API keys
   * @param {number} userId - User ID
   */
  async getUserApiKeys(userId) {
    try {
      const response = await apiClient.get('/api/admin/users/api-keys', {
        params: { user_id: userId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Revoke user API key
   * @param {number} keyId - API key ID
   */
  async revokeApiKey(keyId) {
    try {
      const response = await apiClient.delete(`/api/admin/users/api-keys/${keyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // User Status Management
  // ==========================================

  /**
   * Activate user account
   * @param {number} userId - User ID
   */
  async activateUser(userId) {
    try {
      const response = await apiClient.post('/api/admin/users/activate', {
        id: userId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Suspend user account
   * @param {number} userId - User ID
   * @param {string} reason - Suspension reason
   */
  async suspendUser(userId, reason) {
    try {
      const response = await apiClient.post('/api/admin/users/suspend', {
        id: userId,
        reason: reason
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Statistics
  // ==========================================

  /**
   * Get user statistics
   */
  async getStats() {
    try {
      const response = await apiClient.get('/api/admin/users/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get user summary
   * @param {number} userId - User ID
   */
  async getUserSummary(userId) {
    try {
      const response = await apiClient.get('/api/admin/users/summary', {
        params: { id: userId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new UserService();
